/**
 * Tag Rules › Hours — "Preview on a campaign": pick a country and one of its
 * campaigns, see every enabled hour tag on the 24 hours of the recipients'
 * clock, judged now. Saving a rule refetches it.
 * For Media Devoted | Created By Ciro Autiero
 */

import { trackerFromEnum, trackerLabel } from '@trafficpulse/shared';
import { useState } from 'react';
import { CalendarClock } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, LoadingSpinner } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/utils';

import { HourTagsGrid } from './HourTagsGrid';
import { PreviewPickers } from './PreviewPickers';
import { useHourPerformanceTags } from './useHourPerformanceTags';

function PreviewBody({ countryKey, wiserId }: { countryKey: string; wiserId: number | null }) {
  const { data, isLoading, error } = useHourPerformanceTags(countryKey, wiserId);
  if (!countryKey || wiserId == null) {
    return <p className="text-sm text-muted-foreground">Pick a country and a campaign.</p>;
  }
  if (isLoading) return <LoadingSpinner size="md" />;
  if (error) return <p className="text-sm text-destructive">{getApiErrorMessage(error, 'Could not load the hour tags')}</p>;
  if (!data || data.tagNames.length === 0) {
    return <p className="text-sm text-muted-foreground">No enabled hour tag rule yet.</p>;
  }
  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">
        {trackerLabel(trackerFromEnum(data.provider))} · hours on {data.timezone}
        {data._debug?.failedRanges?.length ? ' · some reports failed — those hours are Unknown' : ''}
      </p>
      <HourTagsGrid data={data} />
      <p className="text-xs text-muted-foreground">
        Filled = tagged · grey = not tagged · dashed = Unknown. Hover a cell for the reason.
      </p>
    </div>
  );
}

const PreviewHeader = () => (
  <CardHeader className="border-b bg-card">
    <div className="flex items-center gap-3">
      <div className="rounded-lg bg-primary/10 p-2">
        <CalendarClock className="h-5 w-5 text-primary" aria-hidden />
      </div>
      <CardTitle className="text-base">Preview on a campaign</CardTitle>
    </div>
  </CardHeader>
);

export function HourTagsPreview() {
  const [countryKey, setCountryKey] = useState('');
  const [wiserId, setWiserId] = useState<number | null>(null);
  return (
    <Card data-testid="hour-tags-preview">
      <PreviewHeader />
      <CardContent className="flex flex-col gap-4 pt-4">
        <PreviewPickers
          countryKey={countryKey}
          wiserId={wiserId}
          onCountry={(key) => {
            setCountryKey(key);
            setWiserId(null);
          }}
          onCampaign={setWiserId}
        />
        <PreviewBody countryKey={countryKey} wiserId={wiserId} />
      </CardContent>
    </Card>
  );
}
